import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Loader2, ArrowRight, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from './CurrencySelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEBOUNCE_MS = 250;
const MIN_QUERY = 2;
const MAX_RESULTS = 6;

/**
 * Debounced auto-complete search input. Calls /api/products?search=… on each
 * input change (after 250 ms of inactivity). Shows up to 6 product preview
 * rows + a "View all results →" link.
 *
 * Replaces the static search input in the global Navbar.
 */
export const SearchAutocomplete = ({ className = '' }) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { format } = useCurrency();
  const T = (fr, en) => (language === 'fr' ? fr : en);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  const localizedName = (p) => (language === 'fr' ? p.name_fr : p.name_en) || p.name;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < MIN_QUERY) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/products`, {
          params: { search: query, limit: MAX_RESULTS },
        });
        setResults(r.data || []);
        setHighlight(-1);
      } catch (_) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const submit = (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    navigate(`/shop?search=${encodeURIComponent(query)}`);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, -1));
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && results[highlight]) {
        e.preventDefault();
        navigate(`/product/${results[highlight].id}`);
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative w-full ${className}`} data-testid="search-autocomplete">
      <form onSubmit={submit}>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0F4C5C]/50 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={onKey}
            placeholder={t('searchPlaceholder')}
            className="w-full h-11 pl-11 pr-12 rounded-full border-2 border-[#0F4C5C]/10 focus:border-[#0F4C5C]/40 focus:outline-none bg-white text-[#0F4C5C] placeholder:text-[#0F4C5C]/40 transition-colors"
            data-testid="search-input"
            aria-autocomplete="list"
            aria-expanded={open}
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(''); setResults([]); }}
              className="absolute right-12 top-1/2 -translate-y-1/2 text-[#0F4C5C]/40 hover:text-[#0F4C5C] transition-colors"
              aria-label="Clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            type="submit"
            className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#0F4C5C] hover:bg-[#0F4C5C]/90 flex items-center justify-center text-white transition-colors"
            aria-label="Search"
            data-testid="search-button"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </button>
        </div>
      </form>

      {open && query.length >= MIN_QUERY && (
        <div
          className="absolute top-full mt-2 left-0 right-0 bg-white rounded-2xl shadow-2xl shadow-[#0F4C5C]/15 border border-[#0F4C5C]/10 overflow-hidden z-50"
          data-testid="search-autocomplete-results"
        >
          {loading && results.length === 0 ? (
            <div className="p-6 flex items-center gap-3 text-sm text-[#0F4C5C]/60">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{T('Recherche...', 'Searching...')}</span>
            </div>
          ) : results.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#0F4C5C]/60">
              {T('Aucun résultat pour', 'No results for')} "<span className="font-semibold text-[#0F4C5C]">{query}</span>"
            </div>
          ) : (
            <>
              <div className="max-h-[420px] overflow-y-auto">
                {results.map((p, idx) => (
                  <button
                    key={p.id}
                    type="button"
                    onMouseEnter={() => setHighlight(idx)}
                    onClick={() => { navigate(`/product/${p.id}`); setOpen(false); }}
                    className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
                      highlight === idx ? 'bg-[#FBF8F3]' : 'hover:bg-[#FBF8F3]'
                    }`}
                    data-testid={`autocomplete-item-${p.id}`}
                  >
                    <img
                      src={p.images?.[0] || 'https://via.placeholder.com/80'}
                      alt=""
                      className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      loading="lazy"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#0F4C5C] truncate">{localizedName(p)}</p>
                      <p className="text-xs text-[#0F4C5C]/60 truncate">{p.vendor_name}</p>
                    </div>
                    <span className="text-sm font-bold text-[#0F4C5C] flex-shrink-0">{format(p.price)}</span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={submit}
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#0F4C5C] text-white font-semibold text-sm hover:bg-[#0F4C5C]/90 transition-colors"
                data-testid="search-autocomplete-view-all"
              >
                {T(`Voir tous les résultats pour "${query}"`, `View all results for "${query}"`)}
                <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
